import { useId, useLayoutEffect, useRef, useState, type JSX, type ReactNode, type RefObject } from "react";
import { dayText, type AnalyticsPoint, type Provider } from "./data.js";

/* ------------------------------------------------------------------ helpers */

function useSize<T extends HTMLElement>(): [RefObject<T | null>, { w: number; h: number }] {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = (): void => {
      const rect = element.getBoundingClientRect();
      const w = Math.round(element.clientWidth || rect.width);
      const h = Math.round(element.clientHeight || rect.height);
      setSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, size];
}

/** 0..top split into `count` round steps (1 / 2 / 2.5 / 5 × 10^k). */
export function niceTicks(max: number, count = 3): number[] {
  const raw = Math.max(max, 1) / count;
  const power = 10 ** Math.floor(Math.log10(raw));
  const step = ([1, 2, 2.5, 5, 10].find((item) => item * power >= raw) ?? 10) * power;
  return Array.from({ length: count + 1 }, (_, index) => index * step);
}

const trim = (value: number): string => String(Number(value.toFixed(1)));
export const tokenTick = (value: number): string => value >= 1e9 ? `${trim(value / 1e9)}B` : value >= 1e6 ? `${trim(value / 1e6)}M` : value >= 1e3 ? `${trim(value / 1e3)}K` : String(Math.round(value));
export const moneyTick = (value: number): string => `$${Math.round(value).toLocaleString("en-US")}`;

const PALETTE: Record<Provider, { top: string; bottom: string; cap: string; line: string; glow: string }> = {
  claude: { top: "#ff4a55", bottom: "#a8121c", cap: "#ff9aa0", line: "#ff3b47", glow: "rgba(255,59,71,.55)" },
  codex: { top: "#3d8bff", bottom: "#123fae", cap: "#9cc6ff", line: "#2f7bff", glow: "rgba(47,123,255,.55)" },
};

function labelStep(count: number, width: number, labelWidth = 34): number {
  const fit = Math.max(2, Math.floor(width / labelWidth));
  return Math.max(1, Math.ceil(count / fit));
}

interface Frame { left: number; right: number; top: number; bottom: number; }

function Axis({ ticks, frame, w, h, format, side = "left" }: { ticks: number[]; frame: Frame; w: number; h: number; format: (value: number) => string; side?: "left" | "right" }): JSX.Element {
  const top = ticks[ticks.length - 1] ?? 1;
  const plotH = h - frame.top - frame.bottom;
  return (
    <g>
      {ticks.map((tick) => {
        const y = frame.top + plotH - (tick / top) * plotH;
        return (
          <g key={tick}>
            {side === "left" && <line className="grid" x1={frame.left} x2={w - frame.right} y1={y} y2={y} />}
            <text className="tick" x={side === "left" ? frame.left - 6 : w - frame.right + 6} y={y + 3.5} textAnchor={side === "left" ? "end" : "start"}>{format(tick)}</text>
          </g>
        );
      })}
    </g>
  );
}

function XLabels({ starts, frame, w, h, slot }: { starts: number[]; frame: Frame; w: number; h: number; slot: number }): JSX.Element {
  const step = labelStep(starts.length, w - frame.left - frame.right);
  return (
    <g>
      {starts.map((start, index) => ((starts.length - 1 - index) % step === 0
        ? <text key={start} className="tick" x={frame.left + index * slot + slot / 2} y={h - 3} textAnchor="middle">{dayText(start)}</text>
        : null))}
    </g>
  );
}

function Box({ className, children }: { className?: string; children: (size: { w: number; h: number }) => ReactNode }): JSX.Element {
  const [ref, size] = useSize<HTMLDivElement>();
  return <div ref={ref} className={`cx-chart ${className ?? ""}`}>{size.w > 0 && size.h > 0 && <svg width={size.w} height={size.h} viewBox={`0 0 ${size.w} ${size.h}`}>{children(size)}</svg>}</div>;
}

/* ------------------------------------------------------------------ bar chart (one provider) */

export function BarChart({ points, provider }: { points: AnalyticsPoint[]; provider: Provider }): JSX.Element {
  const id = useId();
  const colors = PALETTE[provider];
  return (
    <Box>
      {({ w, h }) => {
        const ticks = niceTicks(Math.max(...points.map((point) => point.tokens), 0));
        const frame: Frame = { left: 26, right: 4, top: 6, bottom: 18 };
        const plotW = w - frame.left - frame.right;
        const plotH = h - frame.top - frame.bottom;
        const top = ticks[ticks.length - 1] ?? 1;
        const slot = points.length ? plotW / points.length : plotW;
        const barW = Math.max(2, Math.min(16, slot * (points.length > 40 ? 0.6 : 0.4)));
        return (
          <>
            <defs>
              <linearGradient id={`${id}b`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={colors.top} /><stop offset="1" stopColor={colors.bottom} stopOpacity=".85" /></linearGradient>
            </defs>
            <Axis ticks={ticks} frame={frame} w={w} h={h} format={tokenTick} />
            <g className="bars" style={{ filter: `drop-shadow(0 0 4px ${colors.glow})` }}>
              {points.map((point, index) => {
                const barH = Math.max(1, (point.tokens / top) * plotH);
                const x = frame.left + index * slot + (slot - barW) / 2;
                const y = frame.top + plotH - barH;
                return (
                  <g key={point.start}>
                    <rect x={x} y={y} width={barW} height={barH} rx={Math.min(1.5, barW / 3)} fill={`url(#${id}b)`} />
                    {barH > 6 && <rect x={x} y={y} width={barW} height={Math.min(5, barH * 0.14)} rx={Math.min(1.5, barW / 3)} fill={colors.cap} opacity=".55" />}
                  </g>
                );
              })}
            </g>
            <XLabels starts={points.map((point) => point.start)} frame={frame} w={w} h={h} slot={slot} />
          </>
        );
      }}
    </Box>
  );
}

/* ------------------------------------------------------------------ grouped bars (Claude vs Codex) */

export function GroupedBars({ claude, codex, value }: { claude: AnalyticsPoint[]; codex: AnalyticsPoint[]; value: "tokens" | "costUsd" }): JSX.Element {
  const id = useId();
  return (
    <Box>
      {({ w, h }) => {
        const starts = Array.from(new Set([...claude, ...codex].map((point) => point.start))).sort((a, b) => a - b);
        const lookup = (points: AnalyticsPoint[], start: number): number => points.find((point) => point.start === start)?.[value] ?? 0;
        const ticks = niceTicks(Math.max(...starts.map((start) => Math.max(lookup(claude, start), lookup(codex, start))), 0));
        const format = value === "tokens" ? tokenTick : moneyTick;
        const frame: Frame = { left: value === "tokens" ? 26 : Math.max(26, format(ticks[ticks.length - 1] ?? 0).length * 5.6 + 8), right: 4, top: 6, bottom: 18 };
        const plotW = w - frame.left - frame.right;
        const plotH = h - frame.top - frame.bottom;
        const top = ticks[ticks.length - 1] ?? 1;
        const slot = starts.length ? plotW / starts.length : plotW;
        const barW = Math.max(1.5, Math.min(11, slot * 0.3));
        return (
          <>
            <defs>
              {(["claude", "codex"] as const).map((provider) => (
                <linearGradient key={provider} id={`${id}${provider}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={PALETTE[provider].top} /><stop offset="1" stopColor={PALETTE[provider].bottom} stopOpacity=".8" /></linearGradient>
              ))}
            </defs>
            <Axis ticks={ticks} frame={frame} w={w} h={h} format={format} />
            {starts.map((start, index) => {
              const center = frame.left + index * slot + slot / 2;
              return (["claude", "codex"] as const).map((provider, side) => {
                const barH = Math.max(1, (lookup(provider === "claude" ? claude : codex, start) / top) * plotH);
                return <rect key={`${start}-${provider}`} style={{ filter: `drop-shadow(0 0 3px ${PALETTE[provider].glow})` }} x={side === 0 ? center - barW - 0.8 : center + 0.8} y={frame.top + plotH - barH} width={barW} height={barH} rx={1} fill={`url(#${id}${provider})`} />;
              });
            })}
            <XLabels starts={starts} frame={frame} w={w} h={h} slot={slot} />
          </>
        );
      }}
    </Box>
  );
}

/* ------------------------------------------------------------------ line chart (Token + cost on its own axis) */

const smooth = (pts: Array<[number, number]>): string => {
  if (!pts.length) return "";
  let path = `M${pts[0]![0]},${pts[0]![1]}`;
  for (let index = 1; index < pts.length; index += 1) {
    const p0 = pts[index - 2] ?? pts[index - 1]!;
    const p1 = pts[index - 1]!;
    const p2 = pts[index]!;
    const p3 = pts[index + 1] ?? p2;
    const c1: [number, number] = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2: [number, number] = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    path += ` C${c1[0]},${c1[1]} ${c2[0]},${c2[1]} ${p2[0]},${p2[1]}`;
  }
  return path;
};

export function LineChart({ points, provider }: { points: AnalyticsPoint[]; provider: Provider }): JSX.Element {
  const id = useId();
  const tokenColor = PALETTE[provider];
  const costColor = PALETTE[provider === "claude" ? "codex" : "claude"];
  return (
    <Box>
      {({ w, h }) => {
        const tokenTicks = niceTicks(Math.max(...points.map((point) => point.tokens), 0));
        // Cost gets its own right-hand axis with headroom so the two series read as two lines, not one.
        const costTicks = niceTicks(Math.max(...points.map((point) => point.costUsd), 0) * 1.5);
        const frame: Frame = { left: 30, right: Math.max(24, moneyTick(costTicks[costTicks.length - 1] ?? 0).length * 5.4 + 8), top: 8, bottom: 18 };
        const plotW = w - frame.left - frame.right;
        const plotH = h - frame.top - frame.bottom;
        const slot = points.length ? plotW / points.length : plotW;
        const series = (key: "tokens" | "costUsd", ticks: number[]): Array<[number, number]> => {
          const top = ticks[ticks.length - 1] ?? 1;
          return points.map((point, index) => [frame.left + index * slot + slot / 2, frame.top + plotH - (point[key] / top) * plotH]);
        };
        const lines = [
          { key: "cost", pts: series("costUsd", costTicks), color: costColor },
          { key: "tokens", pts: series("tokens", tokenTicks), color: tokenColor },
        ];
        const base = frame.top + plotH;
        const dots = points.length <= 31;
        return (
          <>
            <defs>
              {lines.map((line) => (
                <linearGradient key={line.key} id={`${id}${line.key}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={line.color.line} stopOpacity=".42" /><stop offset="1" stopColor={line.color.line} stopOpacity="0" /></linearGradient>
              ))}
            </defs>
            <Axis ticks={tokenTicks} frame={frame} w={w} h={h} format={tokenTick} />
            <Axis ticks={costTicks} frame={frame} w={w} h={h} format={moneyTick} side="right" />
            {lines.map((line) => {
              const first = line.pts[0];
              const last = line.pts[line.pts.length - 1];
              if (!first || !last) return null;
              const d = smooth(line.pts);
              return (
                <g key={line.key}>
                  <path d={`${d} L${last[0]},${base} L${first[0]},${base} Z`} fill={`url(#${id}${line.key})`} />
                  <path d={d} fill="none" stroke={line.color.line} strokeWidth="2" style={{ filter: `drop-shadow(0 0 4px ${line.color.glow})` }} />
                  {dots && line.pts.map(([x, y]) => <circle key={x} cx={x} cy={y} r="2.6" fill={line.color.line} stroke="#fff" strokeOpacity=".75" strokeWidth=".8" />)}
                </g>
              );
            })}
            <XLabels starts={points.map((point) => point.start)} frame={frame} w={w} h={h} slot={slot} />
          </>
        );
      }}
    </Box>
  );
}

/* ------------------------------------------------------------------ donut */

export function Donut({ segments, centre, caption }: { segments: Array<{ key: string; value: number; color: string }>; centre: string; caption: string }): JSX.Element {
  const total = segments.reduce((sum, item) => sum + item.value, 0);
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <div className="cx-donut">
      <svg viewBox="0 0 100 100" role="img" aria-label="总用量占比">
        <circle cx="50" cy="50" r={radius} fill="none" stroke="rgba(255,255,255,.07)" strokeWidth="11" />
        {total > 0 && segments.map((segment) => {
          const length = (segment.value / total) * circumference;
          const node = <circle key={segment.key} cx="50" cy="50" r={radius} fill="none" stroke={segment.color} strokeWidth="11" strokeDasharray={`${length} ${circumference - length}`} strokeDashoffset={-offset} transform="rotate(-90 50 50)" style={{ filter: `drop-shadow(0 0 3px ${segment.color})` }} />;
          offset += length;
          return node;
        })}
      </svg>
      <div className="cx-donut-in"><b>{centre}</b><span>{caption}</span></div>
    </div>
  );
}
